<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useArdhisasaAuth } from 'dashboard/composables/useArdhisasaAuth';
import {
  requestPasswordUpdate,
  changeArdhisasaPassword,
} from 'dashboard/api/ardhisasaTickets';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ArdhisasaAuthModal from '../../tickets/components/ArdhisasaAuthModal.vue';

const { t } = useI18n();

const {
  showAuth,
  handleAuthenticated,
  dismissAuth,
  store: authStore,
} = useArdhisasaAuth();

const inputStyles = {
  borderRadius: '0.75rem',
  padding: '0.375rem 0.75rem',
  fontSize: '0.875rem',
  marginBottom: '0.125rem',
};

const step = ref('password'); // 'password' | 'otp'
const currentPassword = ref('');
const newPassword = ref('');
const newPasswordConfirmation = ref('');
const otpCode = ref('');
const loading = ref(false);
const errorMessage = ref('');

const passwordsMismatch = computed(
  () =>
    newPasswordConfirmation.value.length > 0 &&
    newPasswordConfirmation.value !== newPassword.value
);

function extractErrorMessage(err) {
  const data = err?.response?.data;
  const details = data?.details || data?.detail || data?.error;
  if (!details) return t('RESET_PASSWORD.API.ERROR_MESSAGE');
  if (typeof details === 'string') return details;
  const firstField = Object.values(details)[0];
  return Array.isArray(firstField)
    ? firstField[0]
    : t('RESET_PASSWORD.API.ERROR_MESSAGE');
}

function resetForm() {
  step.value = 'password';
  currentPassword.value = '';
  newPassword.value = '';
  newPasswordConfirmation.value = '';
  otpCode.value = '';
}

async function submitCurrentPassword() {
  errorMessage.value = '';
  if (!currentPassword.value) {
    errorMessage.value = t('PROFILE_SETTINGS.FORM.CURRENT_PASSWORD.ERROR');
    return;
  }
  loading.value = true;
  try {
    await requestPasswordUpdate(
      authStore.token,
      authStore.jwt,
      currentPassword.value
    );
    step.value = 'otp';
  } catch (err) {
    errorMessage.value = extractErrorMessage(err);
  } finally {
    loading.value = false;
  }
}

async function submitChangePassword() {
  errorMessage.value = '';
  if (newPassword.value.length < 6) {
    errorMessage.value = t('PROFILE_SETTINGS.FORM.PASSWORD.ERROR');
    return;
  }
  if (passwordsMismatch.value) {
    errorMessage.value = t('PROFILE_SETTINGS.FORM.PASSWORD_CONFIRMATION.ERROR');
    return;
  }
  if (!otpCode.value) {
    errorMessage.value = t('PROFILE_SETTINGS.FORM.OTP_CODE.ERROR');
    return;
  }
  loading.value = true;
  try {
    await changeArdhisasaPassword(authStore.token, authStore.jwt, {
      currentPassword: currentPassword.value,
      password: newPassword.value,
      passwordConfirm: newPasswordConfirmation.value,
      otpCode: otpCode.value,
    });
    useAlert(t('PROFILE_SETTINGS.PASSWORD_UPDATE_SUCCESS'));
    resetForm();
  } catch (err) {
    errorMessage.value = extractErrorMessage(err);
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <div>
    <ArdhisasaAuthModal
      v-if="showAuth"
      @authenticated="handleAuthenticated"
      @close="dismissAuth"
    />

    <!-- Step 1: current password -->
    <form
      v-if="step === 'password'"
      class="flex flex-col w-full gap-4"
      @submit.prevent="submitCurrentPassword"
    >
      <woot-input
        v-model="currentPassword"
        type="password"
        :styles="inputStyles"
        :label="$t('PROFILE_SETTINGS.FORM.CURRENT_PASSWORD.LABEL')"
        :placeholder="$t('PROFILE_SETTINGS.FORM.CURRENT_PASSWORD.PLACEHOLDER')"
      />
      <p v-if="errorMessage" class="text-sm text-n-ruby-9">
        {{ errorMessage }}
      </p>
      <div>
        <NextButton
          type="submit"
          :label="$t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.BTN_TEXT')"
          :is-loading="loading"
          :disabled="loading || !currentPassword"
        />
      </div>
    </form>

    <!-- Step 2: new password + OTP -->
    <form
      v-else
      class="flex flex-col w-full gap-4"
      @submit.prevent="submitChangePassword"
    >
      <p class="text-sm text-n-slate-11">
        {{ $t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.OTP_HINT') }}
      </p>
      <woot-input
        v-model="newPassword"
        type="password"
        :styles="inputStyles"
        :label="$t('PROFILE_SETTINGS.FORM.PASSWORD.LABEL')"
        :placeholder="$t('PROFILE_SETTINGS.FORM.PASSWORD.PLACEHOLDER')"
      />
      <woot-input
        v-model="newPasswordConfirmation"
        type="password"
        :styles="inputStyles"
        :class="{ error: passwordsMismatch }"
        :label="$t('PROFILE_SETTINGS.FORM.PASSWORD_CONFIRMATION.LABEL')"
        :placeholder="
          $t('PROFILE_SETTINGS.FORM.PASSWORD_CONFIRMATION.PLACEHOLDER')
        "
        :error="
          passwordsMismatch
            ? $t('PROFILE_SETTINGS.FORM.PASSWORD_CONFIRMATION.ERROR')
            : ''
        "
      />
      <woot-input
        v-model="otpCode"
        type="text"
        inputmode="numeric"
        :styles="inputStyles"
        :label="$t('PROFILE_SETTINGS.FORM.OTP_CODE.LABEL')"
        :placeholder="$t('PROFILE_SETTINGS.FORM.OTP_CODE.PLACEHOLDER')"
      />
      <p v-if="errorMessage" class="text-sm text-n-ruby-9">
        {{ errorMessage }}
      </p>
      <div class="flex gap-2">
        <NextButton
          type="button"
          faded
          slate
          :label="$t('ARDHISASA.AUTH.BACK')"
          :disabled="loading"
          @click="resetForm"
        />
        <NextButton
          type="submit"
          :label="$t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.OTP_BTN_TEXT')"
          :is-loading="loading"
          :disabled="loading"
        />
      </div>
    </form>
  </div>
</template>
